<template>
  <div class="w-82 self-stretch flex flex-col gap-2">
    <div class="min-h-38 w-full rounded-3xl bg-key">
      <div class="p-4">
        <div>
          <p
            class="text-m-medium"
            :class="triggerSliderDisabled ? 'text-text-disabled' : 'text-black-white'"
          >
            {{ t('itinerary.itineraryTrigger.triggerTravel') }}
          </p>
          <div class="mt-1 flex items-center gap-2 text-s-medium">
            <span :class="triggerSliderDisabled ? 'text-text-disabled' : 'text-black-white'">
              0.1
            </span>
            <div class="relative flex-1" :class="{ 'opacity-25': triggerSliderDisabled }">
              <Slider
                v-model="triggerDisplay"
                :min="0.1"
                :max="3.3"
                :step="0.05"
                :disabled="triggerSliderDisabled"
                class="w-62"
                @value-commit="commitTrigger"
              />
              <div
                class="absolute top-2 left-0 -translate-x-1/2 pointer-events-none text-center leading-none"
                :style="{ left: bubbleLeft }"
              >
                <div class="text-black-white text-s-medium">{{ triggerDisplay[0] }}mm</div>
              </div>
            </div>
            <span :class="triggerSliderDisabled ? 'text-text-disabled' : 'text-black-white'">
              3.3
            </span>
          </div>
        </div>
        <div class="mt-6 group" data-disabled>
          <p class="text-black-white text-m-medium group-data-disabled:text-text-disabled">
            {{ t('itinerary.itineraryTrigger.initialTriggerTravel') }}
          </p>
          <div class="mt-1 flex items-center gap-2 text-s-medium">
            <span class="text-black-white group-data-disabled:text-text-disabled">0.1</span>
            <div class="relative flex-1">
              <Slider
                :model-value="[0]"
                :min="0.1"
                :max="0.3"
                :step="0.05"
                class="w-62"
                track-class="h-3 bg-linear-[var(--color-travel)]"
                range-class="bg-transparent"
                thumb-class="hidden"
              />
            </div>
            <span class="text-black-white group-data-disabled:text-text-disabled">0.3</span>
          </div>
        </div>
      </div>
    </div>
    <div class="h-fit w-full rounded-3xl bg-key">
      <div class="p-4">
        <div class="flex justify-between items-center h-5.5">
          <span class="text-black-white text-m-medium">{{
            t('itinerary.itineraryTrigger.rtMode')
          }}</span>
          <Tooltip
            :content="t('itinerary.itineraryTrigger.rtModeTooltip')"
            side="bottom"
            align="start"
          >
            <div>
              <Switch v-model="rtEnabled" :disabled="rtSwitchDisabled" />
            </div>
          </Tooltip>
        </div>
        <div class="mt-1">
          <p class="text-s-medium text-text-glass-default leading-5">
            {{ t('itinerary.itineraryTrigger.rtModeDescription') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { Slider, Switch, Tooltip } from '@rapoo/element'
  import { useI18n } from 'vue-i18n'

  const props = defineProps<{
    absoluteTrigger: number
    rtTriggerEnable: boolean
    rtSwitchDisabled: boolean
    canEditTrigger: boolean
    triggerSliderDisabled: boolean
  }>()

  const emit = defineEmits<{
    'update:absoluteTrigger': [value: number]
    'update:rtTriggerEnable': [value: boolean]
  }>()

  const { t } = useI18n()

  const triggerDisplay = ref([props.absoluteTrigger / 100])
  watch(
    () => props.absoluteTrigger,
    (val) => {
      triggerDisplay.value = [val / 100]
    },
  )

  const commitTrigger = (val: number[]) => {
    if (!props.canEditTrigger) return
    emit('update:absoluteTrigger', Math.round(val[0] * 100))
  }

  watch(triggerDisplay, (val) => {
    if (val[0] < 0.3) {
      triggerDisplay.value = [0.3]
    }
  })

  const bubbleLeft = computed(() => {
    const ratio = (triggerDisplay.value[0] - 0.1) / (3.3 - 0.1)
    const progress = Math.min(1, Math.max(0, ratio))
    const thumbWidth = 12
    return `calc(${progress * 100}% - ${progress * thumbWidth}px + ${thumbWidth / 2}px)`
  })

  const rtEnabled = computed({
    get: () => props.rtTriggerEnable,
    set: (val: boolean) => {
      emit('update:rtTriggerEnable', val)
    },
  })
</script>

<style scoped></style>
